import React, { useEffect, useState } from "react";
import { Grid, Card, CardContent, Typography, Button, Box, IconButton } from "@mui/material";
import { useDispatch } from "react-redux";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useDropzone } from "react-dropzone";
import { TbCloudUpload } from "react-icons/tb";
import { FiTrash2 } from "react-icons/fi";
import { FILE_MANAGEMENT_API } from "../api/FileManagementApi";
import { updateDocumentInformationData, addNewMessage } from "../slice";
import addImage from "../assets/logo/Vector (2).png";

function BaseCard({ id, title, uploadConfigs, file, disabled, error = "" }) {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const [image, setImage] = useState();
  const [hasimg, setHasImg] = useState(false);
  const [showSubmitError, setShowSubmitError] = useState(error !== "" && file === undefined);

  const { mutate: uploadImage } = useMutation((file) => FILE_MANAGEMENT_API.Upload(file), {
    onSuccess: ({ data: server }, acceptedFiles) => {
      if (server.success) {
        dispatch(
          updateDocumentInformationData({
            key: id + "Id",
            value: server.data.id,
            required: true,
          })
        );
        dispatch(
          updateDocumentInformationData({
            key: id,
            value: server.data,
            required: true,
          })
        );
        const blob = new Blob([acceptedFiles], {
          type: acceptedFiles.type,
        });
        setImage(URL.createObjectURL(blob));
        setHasImg(true);
      }
    },
    onError: ({
      response: {
        data: { message },
      },
    }) => {
      dispatch(
        addNewMessage({
          id: "error-upload",
          text: message,
          type: "error",
        })
      );
      deleteClickHandler();
    },
  });

  const { getRootProps, getInputProps } = useDropzone({
    multiple: false,
    disabled: disabled,
    onDrop: (acceptedFiles, fileRejections) => {
      if (acceptedFiles.length > 0) {
        let selectedFile = acceptedFiles[0];
        if (!uploadConfigs.validUploadFormat.includes(selectedFile.type)) {
          dispatch(
            addNewMessage({
              id: "wrong-file-type",
              text: "نوع فایل انتخابی صحیح نمی‌باشد.",
              type: "error",
            })
          );
          return;
        }
        if (selectedFile.size / 1000 >= uploadConfigs.maximumUploadSize) {
          dispatch(
            addNewMessage({
              id: "wrong-file-type",
              text: `حجم فایل انتخابی  نمی‌تواند بیشتر از ${
                uploadConfigs.maximumUploadSize / 1000
              }مگابایت باشد.`,
              type: "error",
            })
          );
          return;
        }
        uploadImage(acceptedFiles[0]);
      }
    },
  });

  const { mutateAsync: asyncDownloadImage } = useMutation((url) =>
    FILE_MANAGEMENT_API.Download(url)
  );

  useEffect(() => {
    if (file?.fileName) {
      let cachedImage = queryClient.getQueryData([file.fileName]);
      if (cachedImage) {
        setImage(cachedImage);
      } else {
        asyncDownloadImage(file.fileName).then((response) => {
          const blob = new Blob([response.data], {
            type: response.headers["content-type"],
          });
          let img = URL.createObjectURL(blob);
          setImage(img);
          queryClient.setQueryData([file.fileName], img);
        });
      }
    } else {
      setImage();
    }
  }, [file, asyncDownloadImage, queryClient]);

  const deleteClickHandler = () => {
    queryClient.removeQueries([file.fileName]);
    setImage();
    dispatch(
      updateDocumentInformationData({
        key: id,
        value: undefined,
        required: true,
      })
    );
    dispatch(
      updateDocumentInformationData({
        key: id + "Id",
        value: undefined,
        required: true,
      })
    );
    setHasImg(false);
  };

  useEffect(() => {
    setShowSubmitError(error !== "" && file === undefined);
  }, [error, file]);

  return (
    <Card
      sx={{
        width: { xs: "90%", sm: "224px", md: "224px" },
        minWidth: "130px",
        height: "241px",
        paddingLeft: 2,
        borderRadius: 2,
        boxShadow: "none",
        my: { xs: 1, sm: 1, md: 0 },
      }}
    >
      <Box
        sx={{
          position: "relative",
          bgcolor: "#FAFAFA",
          boxSizing: "border-box",
          padding: "1rem",
          borderRadius: 5,
        }}
      >
        <Grid container justifyContent="space-between" flexDirection="row-reverse">
          <Grid item display="flex" flexDirection="column" alignItems="flex=end">
            <Typography variant="h2" textAlign="right" mb={1} display="block" ml="0.2rem">
              {title}
            </Typography>
            <Typography
              variant={"h2"}
              fontSize="0.6rem"
              color="#FF0000"
              sx={{ direction: "rtl", display: "inline-block" }}
              mr="0.2rem"
            >
              {showSubmitError && `(${error}‌)`}
            </Typography>
          </Grid>
          {image && !disabled && (
            <IconButton
              sx={{ position: "absolute", top: "0.5rem", left: "1rem" }}
              onClick={deleteClickHandler}
            >
              <FiTrash2 fontSize="1rem" color="#4C466F" />
            </IconButton>
          )}
        </Grid>
        <CardContent
          sx={{
            bgcolor: "#fff",
            height: "177px",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            padding: "0 !important",
            borderRadius: 2,
          }}
        >
          {image ? (
            <Box width="100%" sx={{ overflow: "hidden", borderRadius: "0.75rem" }}>
              <div {...getRootProps()}>
                <input {...getInputProps()} />
                <Box width="100%">
                  <Box width="100%" height="100%">
                    <div>
                      <img
                        src={image}
                        style={{
                          display: "inline-block",
                          width: "100%",
                          height: "100%",
                          aspectRatio: "1/1",
                          objectFit: "cover",
                        }}
                        alt="uploaded file"
                      />
                    </div>
                  </Box>
                </Box>
              </div>
            </Box>
          ) : (
            !hasimg && (
              <Grid container direction="column" alignItems="center">
                <img src={addImage} style={{ opacity: "0.3", marginBottom: "1rem" }} alt="add" />
                <Box>
                  <div {...getRootProps()}>
                    <input {...getInputProps()} />
                    <Button
                      variant="s2"
                      endIcon={
                        <TbCloudUpload
                          color="#4C466F"
                          style={{ opacity: "0.3", margin: "0 0.2rem" }}
                        />
                      }
                      sx={{ mt: 1 }}
                      disabled={disabled}
                    >
                      بارگذاری مدرک
                    </Button>
                  </div>
                </Box>
              </Grid>
            )
          )}
        </CardContent>
      </Box>
    </Card>
  );
}

export default BaseCard;
